import { memo, useContext } from 'react'
import QRCode from 'react-qr-code'
import styled from 'styled-components'
import storeContext from 'storeContext'
import { truncateDTCLabelSkuName } from 'features/SupplyOrder/pages/XdockV2/DirectToChecker/groupDTCLabelDetails'

type SortingLabelDataType = {
  id: string
  soNumber: string
  hubName: string
  locationType?: string | null
  rackName?: string | null
  waveNumber?: number | null
  sorterName?: string | null
  printedAt: string
  labelType?: never
}

type DTCLabelDataType = {
  id: number | string
  soNumber: string
  hubName: string
  skuColumns: { productName: string; quantity: number }[][]
  printedAt: string
  labelType: 'DTC'
}

type PrintLabelDataType = SortingLabelDataType | DTCLabelDataType

const TemplateSortingLabel = () => {
  const {
    state: {
      printContent: { data = [] },
    },
  } = useContext(storeContext.PrintAreaContext) as unknown as {
    state: { printContent: { data: PrintLabelDataType[] } }
  }

  return (
    <Container>
      <PrintPageStyle />
      {data.map((label, index) =>
        label.labelType === 'DTC' ? (
          // eslint-disable-next-line react/no-array-index-key
          <DTCLabel key={index}>
            <Title>DIRECT TO CHECKER</Title>

            <SoRow>
              <SoQr>
                <QRCode value={label.soNumber} />
              </SoQr>
              <SoInfo>
                <Caption>SO NUMBER</Caption>
                <SoNumber>{label.soNumber.split('/').join('/\u200B')}</SoNumber>
              </SoInfo>
            </SoRow>

            <DtcHubRow>
              <Caption>HUB</Caption>
              <DtcHubName>{label.hubName}</DtcHubName>
            </DtcHubRow>

            <SkuSection>
              <SkuCaption>SKU :</SkuCaption>
              <SkuColumns $isTwoColumn={label.skuColumns.length > 1}>
                {label.skuColumns.map((items) => (
                  <SkuColumn
                    key={items.map(({ productName }) => productName).join('|') || 'empty-column'}
                    $isTwoColumn={label.skuColumns.length > 1}
                  >
                    {items.map(({ productName, quantity }) => (
                      <SkuItem key={`${productName}-${quantity}`}>
                        <SkuName>
                          {truncateDTCLabelSkuName(
                            productName,
                            label.skuColumns.length > 1 ? 26 : 52,
                          )}
                        </SkuName>
                        <SkuQuantity>{quantity}</SkuQuantity>
                      </SkuItem>
                    ))}
                  </SkuColumn>
                ))}
              </SkuColumns>
            </SkuSection>

            <PrintedAt>Printed: {label.printedAt}</PrintedAt>
          </DTCLabel>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <Label key={index}>
            <Title>SORTING LABEL</Title>

            <SoRow>
              <SoQr>
                <QRCode value={label.soNumber} />
              </SoQr>
              <SoInfo>
                <Caption>SO NUMBER</Caption>
                {/* zero-width space after each slash so the number wraps at segment boundaries */}
                <SoNumber>{label.soNumber.split('/').join('/\u200B')}</SoNumber>
              </SoInfo>
            </SoRow>

            <HubRow>
              <Caption>{label.locationType?.toUpperCase()}</Caption>
              <HubName>{label.hubName}</HubName>
            </HubRow>

            <BottomRow>
              <RackInfo>
                {label.rackName ? (
                  <>
                    <RackQr>
                      <QRCode value={label.rackName} />
                    </RackQr>
                    <InfoText>
                      <Caption>RACK</Caption>
                      <InfoValue>{label.rackName}</InfoValue>
                    </InfoText>
                  </>
                ) : null}
              </RackInfo>
              <WaveBox>
                <WaveCaption>WAVE</WaveCaption>
                <WaveNumber>{label.waveNumber ?? 0}</WaveNumber>
              </WaveBox>
            </BottomRow>

            <Footer>
              <SorterRow>
                <Caption>SORTER NAME</Caption>
                <FooterValue>{label.sorterName || '-'}</FooterValue>
              </SorterRow>
              <PrintedAt>Printed: {label.printedAt}</PrintedAt>
            </Footer>
          </Label>
        ),
      )}
    </Container>
  )
}

export default memo(TemplateSortingLabel)

const Container = styled.div`
  width: 100mm;
  margin: 0;
  padding: 0;
`

const Label = styled.div`
  display: grid;
  grid-template-rows: 9mm 35mm minmax(0, 1fr) 30mm 11mm;
  width: 100mm;
  height: 100mm;
  box-sizing: border-box;
  border: 0.4mm solid #000;
  overflow: hidden;
  page-break-after: always;
  page-break-inside: avoid;
  break-inside: avoid;
`

const DTCLabel = styled(Label)`
  grid-template-rows: 9mm 35mm 15mm minmax(0, 1fr) 11mm;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 0.4mm solid #000;
  font-size: 5mm;
  font-weight: 900;
  letter-spacing: 0.6mm;
`

const SoRow = styled.div`
  display: grid;
  grid-template-columns: 31mm minmax(0, 1fr);
  align-items: center;
  gap: 3mm;
  padding: 2mm 3mm;
  border-bottom: 0.4mm solid #000;
`

const SoQr = styled.div`
  width: 29mm;
  height: 29mm;

  svg {
    width: 100%;
    height: 100%;
  }
`

const SoInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5mm;
  min-width: 0;
`

const Caption = styled.div`
  font-size: 2.6mm;
  font-weight: 700;
  letter-spacing: 0.3mm;
  white-space: nowrap;
`

const SoNumber = styled.div`
  font-size: 4.2mm;
  font-weight: 800;
  line-height: 1.25;
  overflow-wrap: anywhere;
`

const HubRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1mm;
  min-height: 0;
  padding: 1.5mm 3mm;
  border-bottom: 0.4mm solid #000;
  overflow: hidden;
`

const HubName = styled.div`
  font-size: 6mm;
  font-weight: 900;
  line-height: 1.15;
  overflow-wrap: break-word;
`
const DtcHubRow = styled(HubRow)`
  overflow: visible;
`

const DtcHubName = styled(HubName)`
  overflow-wrap: anywhere;
`

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32mm;
  border-bottom: 0.4mm solid #000;
`

const RackInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 3mm;
  min-width: 0;
  padding: 2mm 3mm;
`

const RackQr = styled.div`
  flex: 0 0 24mm;
  width: 24mm;
  height: 24mm;

  svg {
    width: 100%;
    height: 100%;
  }
`

const InfoText = styled.div`
  min-width: 0;
`

const InfoValue = styled.div`
  margin-top: 1mm;
  font-size: 3.4mm;
  font-weight: 800;
  line-height: 1.2;
  overflow-wrap: anywhere;
`

const Footer = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) 4mm;
  min-width: 0;
`

const SorterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3mm;
  min-width: 0;
  padding: 0 3mm;
  border-bottom: 0.4mm solid #000;
`

const PrintedAt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4mm;
  font-weight: 600;
  line-height: 1;
`

const FooterValue = styled.div`
  min-width: 0;
  font-size: 3.6mm;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const WaveBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-left: 0.4mm solid #000;
`

const WaveCaption = styled.div`
  font-size: 3mm;
  font-weight: 700;
  letter-spacing: 0.5mm;
`

const WaveNumber = styled.div`
  font-size: 18mm;
  font-weight: 900;
  line-height: 1;
`

const PrintPageStyle = styled.div`
  @media print {
    @page {
      size: 100mm 100mm;
      margin: 0;
    }
  }
`

const SkuSection = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 1mm;
  padding: 2mm 3mm;
  border-bottom: 0.4mm solid #000;
  overflow: hidden;
`

const SkuCaption = styled(Caption)`
  flex: 0 0 auto;
  font-size: 3.2mm;
`

const SkuColumns = styled.div<{ $isTwoColumn: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isTwoColumn }) =>
    $isTwoColumn ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  min-height: 0;
  padding: 0 1.5mm;
  overflow: hidden;
`

const SkuColumn = styled.div<{ $isTwoColumn: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.8mm;
  padding: 0 1.5mm;

  ${({ $isTwoColumn }) =>
    $isTwoColumn
      ? `
          & + & {
            border-left: 0.4mm solid #000;
          }
        `
      : ''}
`

const SkuItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 6mm;
  gap: 1mm;
  min-width: 0;
  font-size: 2.8mm;
  line-height: 1.15;
`

const SkuName = styled.div`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`

const SkuQuantity = styled.div`
  min-width: 0;
  text-align: right;
  white-space: nowrap;
`
